/**
 * Bailey's flash sheet: small tattoo designs drawn as vector paths on a
 * 100 x 100 grid. The same data draws the picker thumbnails (SVG) and the
 * ink on the forearm (canvas), so what you pick is what goes on the arm.
 *
 * Each part is `fill` (solid ink, coloured with Colour ink) or `line`
 * (outline). `tone` picks the colour in traditional flash colours; Black
 * ink fills every tone with black, Fine line drops the fills entirely.
 * Bands wrap all the way round the forearm instead of sitting on one side.
 */
const star = (cx, cy, outer, inner, points = 5) => {
    let d = '';
    for (let i = 0; i < points * 2; i++) {
        const r = i % 2 ? inner : outer;
        const a = -Math.PI / 2 + i * Math.PI / points;
        d += `${i ? 'L' : 'M'}${(cx + Math.cos(a) * r).toFixed(1)} ${(cy + Math.sin(a) * r).toFixed(1)}`;
    }
    return `${d}Z`;
};
const circle = (cx, cy, r) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0Z`;
const sparkle = (cx, cy, r) => `M${cx} ${cy - r}C${cx + r * 0.15} ${cy - r * 0.15} ${cx + r * 0.15} ${cy - r * 0.15} ${cx + r} ${cy}C${cx + r * 0.15} ${cy + r * 0.15} ${cx + r * 0.15} ${cy + r * 0.15} ${cx} ${cy + r}C${cx - r * 0.15} ${cy + r * 0.15} ${cx - r * 0.15} ${cy + r * 0.15} ${cx - r} ${cy}C${cx - r * 0.15} ${cy - r * 0.15} ${cx - r * 0.15} ${cy - r * 0.15} ${cx} ${cy - r}Z`;

export const TATTOOS = [
    { id: 'scissors', name: 'Scissors', parts: [
        { kind: 'fill', tone: 'blue', d: 'M44 60L66 10L73 14L52 64Z' },
        { kind: 'fill', tone: 'blue', d: 'M56 60L34 10L27 14L48 64Z' },
        { kind: 'line', d: `${circle(32, 76, 12)}${circle(68, 76, 12)}` },
        { kind: 'line', d: 'M44 60L66 10L73 14L52 64M56 60L34 10L27 14L48 64' },
        { kind: 'fill', tone: 'yellow', d: circle(50, 46, 4) }
    ] },
    { id: 'comb', name: 'Comb', parts: [
        { kind: 'fill', tone: 'red', d: 'M16 30h68a6 6 0 0 1 6 6v10H10V36a6 6 0 0 1 6-6Z' },
        { kind: 'line', d: 'M16 30h68a6 6 0 0 1 6 6v10H10V36a6 6 0 0 1 6-6ZM16 46v28M24 46v28M32 46v28M40 46v28M48 46v28M56 46v28M64 46v28M72 46v28M80 46v28' }
    ] },
    { id: 'rose', name: 'Rose', parts: [
        { kind: 'fill', tone: 'red', d: 'M50 16c15 0 25 11 23 25-2 13-12 20-23 20s-21-7-23-20c-2-14 8-25 23-25Z' },
        { kind: 'fill', tone: 'green', d: 'M50 80c-10-9-23-5-26 2 11 5 19 5 26-2ZM50 72c10-9 23-5 26 2-11 5-19 5-26-2Z' },
        { kind: 'line', d: 'M50 16c15 0 25 11 23 25-2 13-12 20-23 20s-21-7-23-20c-2-14 8-25 23-25ZM50 29c7 0 10 6 8 10-2 6-10 7-14 2-4-6 0-13 7-13M39 26c-6 7-6 17 2 23M61 26c6 7 6 17-2 23M50 61v31M50 80c-10-9-23-5-26 2 11 5 19 5 26-2ZM50 72c10-9 23-5 26 2-11 5-19 5-26-2Z' }
    ] },
    { id: 'mum', name: 'Mum', parts: [
        { kind: 'fill', tone: 'red', d: 'M50 88C30 74 12 61 12 42c0-12 9-20 19-20 8 0 15 5 19 12 4-7 11-12 19-12 10 0 19 8 19 20 0 19-18 32-38 46Z' },
        { kind: 'fill', tone: 'cream', d: 'M4 44h92l-7 9 7 9H4l7-9-7-9Z' },
        { kind: 'line', d: 'M50 88C30 74 12 61 12 42c0-12 9-20 19-20 8 0 15 5 19 12 4-7 11-12 19-12 10 0 19 8 19 20 0 19-18 32-38 46ZM4 44h92l-7 9 7 9H4l7-9-7-9Z' },
        { kind: 'text', text: 'MUM', x: 50, y: 58, size: 15 }
    ] },
    { id: 'swallow', name: 'Swallow', parts: [
        { kind: 'fill', tone: 'blue', d: 'M47 50C37 35 21 27 4 29c14 6 24 16 30 29ZM53 50C63 35 79 27 96 29c-14 6-24 16-30 29ZM45 74l-9 21 14-12 14 12-9-21Z' },
        { kind: 'fill', tone: 'blue', d: circle(50, 31, 8) },
        { kind: 'fill', tone: 'red', d: 'M50 38c7 0 11 7 11 15 0 9-5 16-11 23-6-7-11-14-11-23 0-8 4-15 11-15Z' },
        { kind: 'line', d: `M47 50C37 35 21 27 4 29c14 6 24 16 30 29ZM53 50C63 35 79 27 96 29c-14 6-24 16-30 29ZM45 74l-9 21 14-12 14 12-9-21ZM50 38c7 0 11 7 11 15 0 9-5 16-11 23-6-7-11-14-11-23 0-8 4-15 11-15Z${circle(50, 31, 8)}` }
    ] },
    { id: 'star', name: 'Star', parts: [
        { kind: 'fill', tone: 'red', d: star(50, 52, 44, 18) },
        { kind: 'fill', tone: 'black', d: 'M50 8L50 52L60.6 37.4Z M91.8 38.4L50 52L67.1 57.6Z M75.9 87.6L50 52L50 70Z M24.1 87.6L50 52L32.9 57.6Z M8.2 38.4L50 52L39.4 37.4Z' },
        { kind: 'line', d: star(50, 52, 44, 18) }
    ] },
    { id: 'moon', name: 'Moon', parts: [
        { kind: 'fill', tone: 'yellow', d: 'M58 12a38 38 0 1 0 26 64 32 32 0 1 1-26-64Z' },
        { kind: 'fill', tone: 'yellow', d: `${sparkle(76, 24, 9)}${sparkle(88, 48, 6)}` },
        { kind: 'line', d: `M58 12a38 38 0 1 0 26 64 32 32 0 1 1-26-64Z${sparkle(76, 24, 9)}${sparkle(88, 48, 6)}` }
    ] },
    { id: 'lightning', name: 'Lightning', parts: [
        { kind: 'fill', tone: 'yellow', d: 'M60 4L22 56h24l-10 40 44-56H56l14-36Z' },
        { kind: 'line', d: 'M60 4L22 56h24l-10 40 44-56H56l14-36Z' }
    ] },
    { id: 'butterfly', name: 'Butterfly', parts: [
        { kind: 'fill', tone: 'blue', d: 'M47 46C39 26 18 14 8 22 2 29 8 45 25 51ZM53 46C61 26 82 14 92 22c6 7 0 23-17 29Z' },
        { kind: 'fill', tone: 'red', d: 'M47 54C35 58 20 70 25 83c6 8 18 2 24-18ZM53 54c12 4 27 16 22 29-6 8-18 2-24-18Z' },
        { kind: 'fill', tone: 'black', d: 'M50 36c3 0 4 4 4 10v26c0 6-2 10-4 10s-4-4-4-10V46c0-6 1-10 4-10Z' },
        { kind: 'line', d: 'M47 46C39 26 18 14 8 22 2 29 8 45 25 51ZM53 46C61 26 82 14 92 22c6 7 0 23-17 29ZM47 54C35 58 20 70 25 83c6 8 18 2 24-18ZM53 54c12 4 27 16 22 29-6 8-18 2-24-18ZM48 36c-4-9-8-13-13-14M52 36c4-9 8-13 13-14' }
    ] },
    { id: 'anchor', name: 'Anchor', parts: [
        { kind: 'fill', tone: 'blue', d: 'M47 18h6v68h-6ZM32 28h36v6H32Z' },
        { kind: 'line', d: `${circle(50, 10, 7)}M50 17v71M32 31h36M12 62c4 19 20 27 38 27s34-8 38-27M12 62l-5 9M12 62l9 4M88 62l5 9M88 62l-9 4` }
    ] },
    { id: 'vine', name: 'Vine band', band: true },
    { id: 'dots', name: 'Dotted band', band: true }
];

export const tattooById = id => TATTOOS.find(t => t.id === id) || null;

// Traditional flash colours. Black ink turns every tone black.
export const TONES = { red: '#c23a2b', green: '#2f7d4f', blue: '#1f5f9e', yellow: '#e2b53a', cream: '#f2e6c8', black: '#1b2328' };
export const INK = '#1b2328';

// Black ink shades its fills as a grey wash, so outlines and details still
// read; only parts that are black in the flash are solid.
const WASH = 'rgba(27, 35, 40, 0.42)';
function inkFor(tone, ink) {
    if (ink === 'colour') return TONES[tone] || INK;
    if (tone === 'cream') return null;
    return tone === 'black' ? INK : WASH;
}

// The parts to draw for an ink style, in order: fills, then lines, then text.
export function tattooLayers(design, ink) {
    if (!design || design.band) return [];
    return design.parts.filter(part => !(ink === 'fineline' && part.kind === 'fill'))
        .map(part => ({ ...part, colour: part.kind === 'fill' ? inkFor(part.tone, ink) : INK, width: ink === 'fineline' ? 2.2 : 4.5 }))
        .filter(part => part.kind !== 'fill' || part.colour);
}

/** SVG markup for a band, for thumbnails. */
export function bandSvgParts(id, ink) {
    const width = ink === 'fineline' ? 2.2 : 4.5;
    if (id === 'vine') {
        const leaf = ink === 'colour' ? TONES.green : INK;
        const leaves = [15, 35, 55, 75].map((x, i) => `M${x} ${50 + (i % 2 ? 6 : -6)}c6 -10 16 -10 18 -4c-6 6 -14 8 -18 4Z`).join('');
        return [
            { kind: 'line', d: 'M0 50C12 38 24 38 37 50S62 62 75 50 88 38 100 50', width, colour: INK },
            ...(ink === 'fineline' ? [{ kind: 'line', d: leaves, width, colour: INK }] : [{ kind: 'fill', d: leaves, colour: ink === 'colour' ? leaf : WASH }, { kind: 'line', d: leaves, width: 2.2, colour: INK }])
        ];
    }
    const dots = [8, 24, 40, 56, 72, 88].map(x => circle(x, 50, ink === 'fineline' ? 3 : 4.5)).join('');
    return [
        { kind: 'line', d: 'M0 36H100M0 64H100', width, colour: INK },
        { kind: 'fill', d: dots, colour: ink === 'colour' ? TONES.red : INK }
    ];
}

function drawParts(ctx, parts) {
    parts.forEach(part => {
        if (part.kind === 'text') {
            ctx.fillStyle = INK;
            ctx.font = `700 ${part.size}px Georgia, 'Times New Roman', serif`;
            ctx.textAlign = 'center';
            ctx.fillText(part.text, part.x, part.y);
            return;
        }
        const path = new Path2D(part.d);
        if (part.kind === 'fill') { ctx.fillStyle = part.colour; ctx.fill(path); }
        else { ctx.strokeStyle = part.colour; ctx.lineWidth = part.width; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.stroke(path); }
    });
}

/**
 * Paint one forearm's ink onto a canvas that wraps the forearm cylinder.
 * `u` is where round the arm the design sits (0 is the front), `v0..v1` is
 * the part of the arm that shows between the cuff and the hand. Designs are
 * drawn twice across the seam so they never get cut in half.
 */
export function paintTattoo(ctx, { id, ink, u, v0, v1, aspect }) {
    const { width: W, height: H } = ctx.canvas;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    const design = tattooById(id);
    if (!design) return false;
    ctx.globalAlpha = 0.92;
    if (design.band) {
        // A band around the wrist end of the visible arm.
        const h = (v1 - v0) * H * 0.36;
        const y = v0 * H + (v1 - v0) * H * 0.62 - h / 2;
        const tiles = 6;
        const tileW = W / tiles;
        for (let i = 0; i < tiles; i++) {
            ctx.save();
            ctx.translate(i * tileW, y);
            ctx.scale(tileW / 100, h / 100);
            drawParts(ctx, bandSvgParts(id, ink));
            ctx.restore();
        }
    } else {
        // Square on the skin: the canvas is `aspect` times wider per unit of
        // arm than it is tall, so the design is stretched to match.
        const h = (v1 - v0) * H * 0.96;
        const w = h * aspect;
        const y = (v0 + v1) / 2 * H - h / 2;
        [0, -W, W].forEach(offset => {
            ctx.save();
            ctx.translate(u * W - w / 2 + offset, y);
            ctx.scale(w / 100, h / 100);
            drawParts(ctx, tattooLayers(design, ink));
            ctx.restore();
        });
    }
    ctx.globalAlpha = 1;
    return true;
}
